import { estados } from "./data";

// Busca e geocodificacao reversa de endereco pelo Nominatim (OpenStreetMap).
//
// Substitui o Geocoder/Places do Google, que exigem chave e faturamento. O
// Nominatim e gratuito, mas e um servico publico mantido por voluntarios: a
// politica de uso pede no maximo 1 requisicao por segundo e que o cliente se
// identifique (o navegador ja envia o Referer). Por isso tudo aqui passa por UMA
// fila com intervalo minimo e por um cache em memoria.
//
// IMPORTANTE para quando o sistema sair da demonstracao: para uso real em
// escala o certo e um provedor proprio ou pago (Nominatim proprio, Photon,
// Mapbox, etc.). Trocar e so reescrever este arquivo -- o resto da tela so
// conhece `buscarEnderecos` e `enderecoPorCoordenada`.

const BASE = "https://nominatim.openstreetmap.org";
const INTERVALO_MINIMO_MS = 1100;

export class ErroGeocodificacao extends Error {
  // tipo: 'rede' (sem resposta), 'limite' (429/503), 'servico' (outro erro HTTP)
  constructor(tipo, mensagem) {
    super(mensagem);
    this.name = "ErroGeocodificacao";
    this.tipo = tipo;
  }
}

const cacheBusca = new Map();
const cacheReversa = new Map();
let ultimaChamada = 0;
let fila = Promise.resolve();

const dorme = (ms, signal) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener("abort", () => {
        clearTimeout(t);
        reject(new DOMException("Cancelado", "AbortError"));
      }, { once: true });
    }
  });

// Serializa as chamadas e garante o intervalo minimo entre elas.
function naFila(tarefa, signal) {
  const rodar = async () => {
    if (signal?.aborted) throw new DOMException("Cancelado", "AbortError");
    const espera = ultimaChamada + INTERVALO_MINIMO_MS - Date.now();
    if (espera > 0) await dorme(espera, signal);
    ultimaChamada = Date.now();
    return tarefa();
  };
  const resultado = fila.then(rodar, rodar);
  // A fila segue mesmo que esta tarefa falhe.
  fila = resultado.catch(() => {});
  return resultado;
}

async function requisitar(url, signal) {
  let resposta;
  try {
    resposta = await fetch(url, { signal, headers: { Accept: "application/json" } });
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    throw new ErroGeocodificacao("rede", "Sem conexão com o serviço de endereços.");
  }
  if (resposta.status === 429 || resposta.status === 503) {
    throw new ErroGeocodificacao("limite", "O serviço de endereços está ocupado. Tente novamente em instantes.");
  }
  if (!resposta.ok) {
    throw new ErroGeocodificacao("servico", `O serviço de endereços respondeu ${resposta.status}.`);
  }
  try {
    return await resposta.json();
  } catch (e) {
    throw new ErroGeocodificacao("servico", "Resposta inválida do serviço de endereços.");
  }
}

const semAcento = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

const ESTADOS_POR_CHAVE = new Map(estados.map((e) => [semAcento(e), e]));

// "Espirito Santo" / "espírito santo" / "State of Espírito Santo" -> "Espírito Santo".
// Devolve '' quando nao reconhece: quem chama trata como "falta informar".
export function normalizarEstado(bruto) {
  const chave = semAcento(bruto).replace(/^(state of|estado de|estado do|estado da)\s+/, "");
  return ESTADOS_POR_CHAVE.get(chave) || "";
}

// O Nominatim no Brasil devolve em `county` coisas como "Região Geográfica
// Imediata de Vitória", que NAO e cidade. So aceitamos county se nao for isso.
const ehRegiaoGeografica = (t) => /^regi[aã]o\s+(geogr[aá]fica|metropolitana|integrada)/i.test(String(t || "").trim());

export function extrairLocal(item) {
  const a = item?.address || {};
  const candidatas = [a.city, a.town, a.village, a.municipality, a.county];
  const cidade = candidatas.find((c) => c && !ehRegiaoGeografica(c)) || "";
  const estado = normalizarEstado(a.state);
  return { cidade, estado };
}

function paraResultado(item) {
  const lat = parseFloat(item.lat);
  const lng = parseFloat(item.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const { cidade, estado } = extrairLocal(item);
  return {
    id: String(item.place_id ?? `${lat},${lng}`),
    descricao: item.display_name || "",
    endereco: item.display_name || "",
    lat,
    lng,
    cidade,
    estado,
  };
}

// Busca por texto. Devolve [] quando nao ha resultado (isso NAO e erro).
export async function buscarEnderecos(texto, { signal } = {}) {
  const termo = String(texto || "").trim();
  if (termo.length < 3) return [];

  const chave = semAcento(termo);
  if (cacheBusca.has(chave)) return cacheBusca.get(chave);

  const url = `${BASE}/search?format=jsonv2&addressdetails=1&countrycodes=br&limit=6&accept-language=pt-BR&q=${encodeURIComponent(termo)}`;
  const dados = await naFila(() => requisitar(url, signal), signal);

  const resultados = (Array.isArray(dados) ? dados : []).map(paraResultado).filter(Boolean);
  cacheBusca.set(chave, resultados);
  return resultados;
}

// Coordenada -> endereco/cidade/estado. Devolve null quando o ponto nao tem
// endereco conhecido (ex.: no mar); erro de rede/servico vira ErroGeocodificacao.
export async function enderecoPorCoordenada(lat, lng, { signal } = {}) {
  const la = Number(lat);
  const lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;

  // ~11 m de precisao: cliques proximos reaproveitam a resposta.
  const chave = `${la.toFixed(4)},${lo.toFixed(4)}`;
  if (cacheReversa.has(chave)) return cacheReversa.get(chave);

  const url = `${BASE}/reverse?format=jsonv2&addressdetails=1&zoom=16&accept-language=pt-BR&lat=${la}&lon=${lo}`;
  const dados = await naFila(() => requisitar(url, signal), signal);

  if (!dados || dados.error) {
    cacheReversa.set(chave, null);
    return null;
  }
  const { cidade, estado } = extrairLocal(dados);
  const resultado = { endereco: dados.display_name || "", cidade, estado };
  cacheReversa.set(chave, resultado);
  return resultado;
}

// Texto de fallback para o campo `endereco` (obrigatorio no backend) quando o
// servico de endereco nao respondeu e a pessoa so marcou o ponto no mapa.
export function enderecoPorCoordenadas(lat, lng) {
  return `Coordenadas ${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
}
